/**
 * @file use-media-query.ts
 *
 * React hook for matching named Gravity breakpoints. Wraps the browser
 * `window.matchMedia` API and keeps the result in sync with resize events so
 * components re-render only when the active breakpoint changes. Ported from the
 * design-reviewed Website Builder v2 implementation.
 */

import { useEffect, useState } from 'react';

export type ScreenType = 'Mobile' | 'Desktop' | 'Tablet';

const screenTypeQueries: Record<ScreenType, string> = {
  Mobile: '(max-width: 599px)',
  Tablet: '(min-width: 600px) and (max-width: 767px)',
  Desktop: '(min-width: 768px)',
};

/**
 * Tracks whether the named Gravity breakpoint currently matches.
 *
 * @param screenType - The named breakpoint (`Mobile`, `Tablet`, or `Desktop`).
 *
 * @returns `true` while the breakpoint's media query matches the viewport.
 */
export function useMediaQuery(screenType: ScreenType): boolean {
  const query = screenTypeQueries[screenType];
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);
    const handleChange = (e: MediaQueryListEvent): void => setMatches(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
}
